/* ============================================================
   The corpus stating what it is, and offering itself in a form that
   leaves the site.

   Three controls, and the ordering is the argument: the edition first,
   because it is what makes the other two worth anything. A CSV with no
   edition in it is a set of numbers someone will still be quoting in
   eighteen months with no way to tell they are stale.

   The caveat travels with the data. Road bands are derived rather than
   observed, and a band read out of a spreadsheet with its provenance
   column ignored is exactly how "derived" becomes "measured" in
   somebody else's deck. So provenance is a column, the derivation is a
   column, and the header block says it in words before either.
   ============================================================ */

import { html, raw } from '../shared/html.js';
import { icon } from '../shared/icons.js';
import { toast } from '../shared/toast.js';
import { benchmarkEdition, benchmarkCitation, benchmarkRows } from '../data/edition.js';

function csvCell(value) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function buildCsv(edition, url) {
  const rows = benchmarkRows();
  const head = Object.keys(rows[0]);
  const lines = [
    ['Kanda Logistics Advisory — Corridor Cost Benchmarks'],
    ['Edition', `${edition.year}.${edition.seq}`, 'Digest', edition.digest],
    ['Corridors', edition.corridors, 'Bands', edition.bands],
    ['Struck as at', edition.asOf],
    ['Source', url],
    [
      'Caveat',
      'Road bands are DERIVED from published rate guidance, not observed transactions. ' +
        'Rail bands are published tariffs covering LINE HAUL ONLY — add first/last mile, ' +
        'lift charges and ICD storage before comparing rail to road.'
    ],
    [],
    head
  ];
  for (const row of rows) lines.push(head.map((k) => row[k]));
  return lines.map((line) => line.map(csvCell).join(',')).join('\r\n');
}

export function EditionStrip() {
  const edition = benchmarkEdition();
  const url = `${window.location.origin}/about`;

  const node = document.createElement('div');
  node.className = 'edition-strip';
  node.innerHTML = html`
    <div class="edition-meta">
      <span class="edition-label">${edition.label}</span>
      <p>
        The code after the counts is derived from the bands themselves, so two people quoting
        this edition are quoting the same numbers. If yours differs from a colleague's, one of
        you is reading a cached copy — this site works offline, so that happens.
      </p>
    </div>
    <div class="edition-actions no-print">
      <button type="button" class="btn btn-sm" id="edition-cite">
        ${raw(icon('document', 14))}<span>Copy citation</span>
      </button>
      <button type="button" class="btn btn-sm" id="edition-csv">
        ${raw(icon('download', 14))}<span>Download this edition (.csv)</span>
      </button>
    </div>
  `;

  node.querySelector('#edition-cite').addEventListener('click', async () => {
    const text = benchmarkCitation(edition, url);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(text);
      toast('Citation copied.');
    } catch {
      /* Clipboard is unavailable on insecure origins and older mobile
         browsers. Show the text rather than report a failure — it
         exists either way and can be selected by hand. */
      const box = document.createElement('textarea');
      box.readOnly = true;
      box.rows = 4;
      box.value = text;
      box.setAttribute('aria-label', 'Citation for this edition');
      box.style.cssText = 'width:100%;margin-top:var(--ca-space-2);font-size:var(--ca-text-xs);';
      node.querySelector('.edition-actions').after(box);
      box.focus();
      box.select();
    }
  });

  node.querySelector('#edition-csv').addEventListener('click', () => {
    // BOM so Excel reads the em-dashes in corridor names as UTF-8.
    const blob = new Blob(['﻿' + buildCsv(edition, url)], {
      type: 'text/csv;charset=utf-8'
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `kanda-corridor-benchmarks-${edition.year}.${edition.seq}-${edition.digest}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(a.href);
      a.remove();
    }, 0);
  });

  return node;
}
